/* eslint-disable no-underscore-dangle */
import { gql, useLazyQuery } from "@apollo/client";
import { captureEvent } from "@sentry/nextjs";
import { useEffect, useState } from "react";
import {
  CheckMultiBind,
  CheckMultiBindVariables,
} from "../../../../../__generated__/CheckMultiBind";
import { isDefined } from "utils/utils";

let POLL_INTERVAL = 750;
let POLL_TIMEOUT = 45000;

type Status = "complete" | "pending" | "failed" | "partial";

function useCheckBind({
  onTimeout,
  onPollingDone = () => {},
}: {
  onTimeout: () => void;
  onPollingDone?: () => void;
}) {
  const [pollTimeout, setPollTimeout] = useState<number>();
  const [checkMultiBind, { data, startPolling, stopPolling }] = useLazyQuery<
    CheckMultiBind,
    CheckMultiBindVariables
  >(CHECK_MULTI_BIND);

  const results = data?.checkMultiBind;

  let status: Status =
    (results?.length ?? 0) > 0 &&
    results?.every((policy) => isDefined(policy.policy))
      ? "complete"
      : "pending";

  useEffect(() => {
    if (status === "complete") {
      clearTimeout(pollTimeout);

      stopPolling();
      onPollingDone();
      setPollTimeout(undefined);
    }
  }, [pollTimeout, status]);

  return {
    pollPolicy: async (draftId: string) => {
      await checkMultiBind({
        variables: {
          input: {
            draftId,
          },
        },
      });

      startPolling(POLL_INTERVAL);

      setPollTimeout(
        setTimeout(() => {
          stopPolling();
          setPollTimeout(undefined);
          onPollingDone();
          status = results?.some(isDefined) ? "partial" : "failed";

          captureEvent({
            message: `Check bind poll timeout`,
            extra: { draftId },
          });

          onTimeout();
        }, POLL_TIMEOUT)
      );
    },
    isPollingPolicy: !!pollTimeout,
    results,
    status,
  };
}

const CHECK_MULTI_BIND = gql`
  query CheckMultiBind($input: CheckMultiBindInput) {
    checkMultiBind(input: $input) {
      _id
      policy {
        _id
        name
        netPremium
        termPremium
        billingInterval
        billingDay
        total
        deductible
        effectiveDate
        limit
        policyNumber
        client {
          _id
          email
          metadata {
            municipality
          }
        }
        metadata {
          discount
          cancelPreviousInsurance
          previousInsurance
        }
        copays {
          total
        }
        ratingsData {
          name
          breed
          lifeLimit
          birthday
          sex
          copay
          breedersAddOn
          lifeUtilityAddOn
          access
        }
        discounts {
          maxInvoicesSpread
          name
          type
          total
        }
        currency {
          name
          code
          symbol
        }
      }
    }
  }
`;

export default useCheckBind;
